import React from "react";
import { Card, Stack } from "react-bootstrap";
import { School, SCHOOL_COLORS } from "../../../../util/cardEffects/effectTypes.ts";
import { useCardScale } from "../../../../util/cardUtils.ts";

let zIndex = 1;

interface PlayerInfoCardProps {
    name: string;
    health: number;
    maxHealth?: number;
    school: School;
    poolFocus: number;
    poolTempFocus?: number;
    nextTurnFocus: number;
    nextTurnTempFocus?: number;
    ritualIntensity?: number;
    deckCount: number;
    discardCount: number;
    scale?: number;
    verticalIndex?: number;
    highlight?: boolean;
    inline?: boolean;
}

// Base dimensions at scale = 1 (wider, shorter than playing card)
export const BASE_PLAYER_WIDTH = 160;
export const BASE_PLAYER_HEIGHT = BASE_PLAYER_WIDTH * 0.625; // 16:10 ratio

export const PlayerInfoCard: React.FC<PlayerInfoCardProps> = ({
    name,
    health,
    school,
    poolFocus,
    poolTempFocus = 0,
    nextTurnFocus,
    nextTurnTempFocus = 0,
    ritualIntensity = 0,
    deckCount,
    discardCount,
    scale = 1,
    verticalIndex = zIndex++,
    highlight = false,
    inline = true,
}) => {
    const baseScale = useCardScale();

    const adjustedScale = baseScale * scale;

    const schoolColor = SCHOOL_COLORS[school];

    const renderStarRow = (
        label: string,
        focus: number,
        tempFocus: number,
    ) => {
        const stars = [];

        for (let i = 0; i < 10; i++) {

            const starSize = 4;
            const focusStar = (key: number, shape: number = 0, white: boolean = false, transparent: boolean = false) => {
                return <div
                    key={key}
                    className={shape === 0 ? "circle" : (shape === 1 ? "four-star" : "heptogram")}
                    style={{
                        width: 0,
                        height: 0,
                        borderLeft: `${starSize}px solid transparent`,
                        borderRight: `${starSize}px solid transparent`,
                        borderBottom: `${starSize * 2}px solid ${white ? "#fff" : schoolColor}`,
                        marginRight: `2px`,
                        position: "relative",
                        backgroundColor: `${white ? "#fff" : schoolColor}`,
                        opacity: transparent ? 0.5 : 1,
                    }}
                />
            }

            let shape = 0;
            if (i < focus) shape++;
            if (i < focus + tempFocus) shape++;
            stars.push(
                focusStar(i, shape, shape <= 0, shape === 0)
            );
        }

        if (focus + tempFocus > 10) {
            stars.push(
                <div
                    key="overflow"
                    style={{
                        fontSize: `1px`,
                        color: schoolColor,
                    }}
                >
                    +{focus + tempFocus - 10}
                </div>
            );
        }

        return (
            <div
                style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: `1px`,
                }}
            >
                <div
                    style={{
                        color: "#9ca3af",
                        fontSize: `10px`,
                        textTransform: "uppercase",
                        letterSpacing: `1px`,
                        fontWeight: "bold",
                    }}
                >
                    {label}
                </div>
                <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap" }}>
                    {stars}
                </div>
            </div>
        );
    };

    return (
        <div style={{
            width: `${Math.ceil(BASE_PLAYER_WIDTH * adjustedScale)}px`,
            height: `${BASE_PLAYER_HEIGHT * adjustedScale}px`,
            overflow: "hidden",
            boxShadow: highlight ? `0 ${2 * adjustedScale}px ${10 * adjustedScale}px ${schoolColor}40` : "",
            display: inline ? "inline-block" : "",
            borderRadius: `${6 * adjustedScale}px`
        }}>
            <Card
                style={{
                    transformOrigin: "top left",
                    width: `${BASE_PLAYER_WIDTH}px`,
                    height: `${BASE_PLAYER_HEIGHT}px`,
                    position: "relative",
                    backgroundColor: "#1f2937",
                    border: highlight ? `2px solid ${schoolColor}` : "",
                    borderRadius: `6px`,
                    overflow: "hidden",
                    zIndex: verticalIndex,
                    transform: `scale(${adjustedScale})`,
                }}
            >
                <Card.Body
                    style={{
                        position: "relative",
                        padding: `8px`,
                        display: "flex",
                        flexDirection: "column",
                        height: "100%",
                    }}
                >
                    {/* Top Row: Name, Health, Deck/Discard */}
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "flex-start",
                            marginBottom: `6px`,
                        }}
                    >
                        {/* Name + School Dot */}
                        <div style={{ display: "flex", alignItems: "center", gap: `4px` }}>
                            <div>
                                <div
                                    style={{
                                        color: schoolColor,
                                        fontSize: `15px`,
                                        fontWeight: "bold",
                                        textShadow: `0 0 3px ${schoolColor}60`,
                                    }}
                                >
                                    {name}
                                </div>
                            </div>
                        </div>

                        {/* Deck / Discard */}
                        <div
                            style={{
                                position: "absolute",
                                right: 8,
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "flex-end",
                                gap: `1px`,
                            }}
                        >
                            <div
                                style={{
                                    color: "#e5e7eb",
                                    fontSize: `10px`,
                                    display: "flex",
                                    alignItems: "center",
                                    gap: `1px`,
                                }}
                            >
                                {health}
                                <span style={{ color: "#ef4444" }}>♡</span>
                            </div>
                            <div
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: `1px`,
                                    color: "#e5e7eb",
                                    fontSize: `10px`,
                                }}
                            >
                                {deckCount}
                                <span style={{ color: schoolColor }}>🂡</span>
                            </div>
                            <div
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: `1px`,
                                    color: "#9ca3af",
                                    fontSize: `10px`,
                                }}
                            >
                                {discardCount}
                                <span style={{ color: schoolColor }}>🂠</span>
                            </div>
                            <div
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: `1px`,
                                    color: "#9ca3af",
                                    fontSize: `10px`,
                                }}
                            >
                                {ritualIntensity}
                                <span style={{ color: schoolColor }}>§</span>
                            </div>
                        </div>
                    </div>

                    {/* Star Rows */}
                    <Stack gap={1} style={{ flex: 1, justifyContent: "flex-end" }}>
                        {renderStarRow("Next", nextTurnFocus, nextTurnTempFocus)}
                        {renderStarRow("Pool", poolFocus, poolTempFocus)}
                    </Stack>
                </Card.Body>
            </Card>
        </div>
    );
};
